import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Scan,
  Target,
  Play,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Server,
  Globe,
  History,
  Settings,
  ChevronDown,
  ChevronUp,
  Shield,
  Bug
} from 'lucide-react';
import api from '../lib/api';

export default function VulnerabilityScannerPage() {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Scan configuration
  const [scanConfig, setScanConfig] = useState({
    name: '',
    scan_type: 'full',
    target_urls: '',
    target_ips: '',
    scan_depth: 'standard',
    schedule_enabled: false
  });

  // Quick scan target
  const [quickTarget, setQuickTarget] = useState('');

  useEffect(() => {
    fetchScanHistory();
  }, []);

  const fetchScanHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/vulnerabilities/scanner/history');
      setScanHistory(response.data);
    } catch (error) {
      console.error('Failed to load scan history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const runQuickScan = async () => {
    if (!quickTarget.trim()) {
      toast.error('Please enter a target URL or IP address');
      return;
    }

    setScanning(true);
    setScanResults(null);
    
    try {
      const isUrl = quickTarget.includes('http') || quickTarget.includes('.');
      const params = isUrl ? { target_url: quickTarget } : { target_ip: quickTarget };
      
      const response = await api.post('/vulnerabilities/scanner/quick-scan', null, { params });
      setScanResults(response.data);
      toast.success(`Scan complete! Found ${response.data.vulnerabilities_found} vulnerabilities`);
      fetchScanHistory();
    } catch (error) {
      toast.error('Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const runFullScan = async () => {
    if (!scanConfig.name.trim()) {
      toast.error('Please enter a scan name');
      return;
    }

    setScanning(true);
    setScanResults(null);

    try {
      const config = {
        name: scanConfig.name,
        scan_type: scanConfig.scan_type,
        target_urls: scanConfig.target_urls.split('\n').filter(u => u.trim()),
        target_assets: scanConfig.target_ips.split('\n').filter(i => i.trim()),
        scan_depth: scanConfig.scan_depth,
        schedule_enabled: scanConfig.schedule_enabled,
        check_categories: []
      };

      const response = await api.post('/vulnerabilities/scanner/full-scan', config);
      setScanResults(response.data);
      toast.success(`Full scan complete! Found ${response.data.vulnerabilities_found} vulnerabilities`);
      fetchScanHistory();
    } catch (error) {
      toast.error('Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white'
    };
    return colors[severity] || 'bg-slate-400 text-white';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vulnerability-scanner">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Vulnerability Scanner</h1>
            <p className="text-slate-500 mt-1">Scan your assets for security vulnerabilities</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/vulnerabilities')}>
            <Shield className="w-4 h-4 mr-2" />
            View All Vulnerabilities
          </Button>
        </div>

        {/* Quick Scan */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-blue-600" />
              Quick Scan
            </CardTitle>
            <CardDescription>
              Perform a rapid vulnerability assessment on a single target
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Enter URL (https://example.com) or IP address (192.168.1.1)"
                  value={quickTarget}
                  onChange={(e) => setQuickTarget(e.target.value)}
                  disabled={scanning}
                  data-testid="quick-scan-input"
                />
              </div>
              <Button onClick={runQuickScan} disabled={scanning} data-testid="quick-scan-btn">
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Quick Scan
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Quick scan performs a lightweight vulnerability check in under 60 seconds
            </p>
          </CardContent>
        </Card>

        {/* Full Scan Configuration */}
        <Card>
          <CardHeader>
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  Full Scan Configuration
                </CardTitle>
                <CardDescription>
                  Configure and run a comprehensive vulnerability assessment
                </CardDescription>
              </div>
              {showAdvanced ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </CardHeader>
          {showAdvanced && (
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Scan Name</label>
                  <Input
                    placeholder="Production Environment Scan"
                    value={scanConfig.name}
                    onChange={(e) => setScanConfig({ ...scanConfig, name: e.target.value })}
                    data-testid="scan-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Scan Depth</label>
                  <select
                    value={scanConfig.scan_depth}
                    onChange={(e) => setScanConfig({ ...scanConfig, scan_depth: e.target.value })}
                    className="w-full h-10 px-3 border rounded-md"
                    data-testid="scan-depth-select"
                  >
                    <option value="light">Light - Quick checks only</option>
                    <option value="standard">Standard - Balanced coverage</option>
                    <option value="deep">Deep - Comprehensive analysis</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Target URLs (one per line)</label>
                  <textarea
                    placeholder="https://app.example.com&#10;https://api.example.com"
                    value={scanConfig.target_urls}
                    onChange={(e) => setScanConfig({ ...scanConfig, target_urls: e.target.value })}
                    className="w-full h-24 px-3 py-2 border rounded-md text-sm"
                    data-testid="target-urls-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Target IPs/Ranges (one per line)</label>
                  <textarea
                    placeholder="192.168.1.0/24&#10;10.0.0.1"
                    value={scanConfig.target_ips}
                    onChange={(e) => setScanConfig({ ...scanConfig, target_ips: e.target.value })}
                    className="w-full h-24 px-3 py-2 border rounded-md text-sm"
                    data-testid="target-ips-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scanConfig.schedule_enabled}
                    onChange={(e) => setScanConfig({ ...scanConfig, schedule_enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Schedule recurring scans</span>
                </label>
              </div>

              <Button onClick={runFullScan} disabled={scanning} className="w-full" data-testid="full-scan-btn">
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running Full Scan...
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 mr-2" />
                    Start Full Scan
                  </>
                )}
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Scan Results */}
        {scanResults && (
          <Card className="border-2 border-blue-200">
            <CardHeader className="bg-blue-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Scan Results - {scanResults.scan_id}
                </CardTitle>
                <Badge variant="outline">{scanResults.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{scanResults.assets_scanned}</p>
                  <p className="text-xs text-slate-500">Assets Scanned</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{scanResults.critical_count}</p>
                  <p className="text-xs text-slate-500">Critical</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{scanResults.high_count}</p>
                  <p className="text-xs text-slate-500">High</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{scanResults.medium_count}</p>
                  <p className="text-xs text-slate-500">Medium</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{scanResults.low_count}</p>
                  <p className="text-xs text-slate-500">Low</p>
                </div>
              </div>

              {/* Findings List */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Findings ({scanResults.findings?.length || 0})</h4>
                {scanResults.findings?.map((finding, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`finding-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <Bug className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">{finding.title}</p>
                        <p className="text-sm text-slate-500">
                          {finding.attack_type?.replace(/_/g, ' ')} • {finding.affected_asset}
                          {finding.cve_id && ` • ${finding.cve_id}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {finding.cvss_score && (
                        <Badge variant="outline">CVSS: {finding.cvss_score}</Badge>
                      )}
                      <Badge className={getSeverityColor(finding.severity)}>
                        {finding.severity}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Duration: {scanResults.duration_seconds}s
                </p>
                <Button variant="outline" onClick={() => navigate('/vulnerabilities')}>
                  View All Vulnerabilities
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scan History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Scan History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : scanHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Scan className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No scan history yet</p>
                <p className="text-sm mt-1">Run your first scan to see results here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scanHistory.map((scan) => (
                  <div 
                    key={scan.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {scan.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : scan.status === 'running' ? (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{scan.scan_name || scan.id}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(scan.started_at).toLocaleString()} • {scan.assets_scanned} assets
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold">{scan.vulnerabilities_found} vulns</p>
                        <p className="text-xs text-slate-500">
                          {scan.critical_count} critical, {scan.high_count} high
                        </p>
                      </div>
                      <Badge variant="outline">{scan.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800">Scanner Note</h4>
                <p className="text-sm text-amber-700 mt-1">
                  This vulnerability scanner is running in <strong>simulation mode</strong>. 
                  It generates realistic vulnerability findings for demonstration purposes. 
                  For production use, integrate with real vulnerability scanning tools like Nessus, Qualys, or OpenVAS.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
